// v2/components/ProductRow.js
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { taslakYaz } from '../utils/miktarTaslak';
import { Minus, Plus, ShoppingCart, Package, Pin, Eye, Check, CheckCircle2, XCircle } from 'lucide-react';
import { formatTRY, formatNumber } from '../utils/format';
import { WAREHOUSES } from '../constants/catalog';
import styles from './ProductTable.module.css';

/** Tablo fiyati: fiyat yoksa sadece '-' (para birimi isareti yok) */
const fiyatYok = (v) =>
  v === null || v === undefined || v === '' || !Number.isFinite(Number(v));
const fiyatTRY = (v) => (fiyatYok(v) ? '-' : formatTRY(v));

/** Basili tutunca hizlanarak tekrar eden aksiyon */
function useHoldRepeat(action) {
  const timer = useRef(null);
  const count = useRef(0);

  const stop = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = null;
    count.current = 0;
  }, []);

  useEffect(() => stop, [stop]);

  const start = useCallback(() => {
    action();
    const tick = () => {
      count.current += 1;
      action();
      timer.current = setTimeout(tick, count.current > 6 ? 40 : 90);
    };
    timer.current = setTimeout(tick, 400);
  }, [action, stop]);

  return {
    onPointerDown: (e) => { if (e.button === 0 || e.pointerType !== 'mouse') start(); },
    onPointerUp: stop,
    onPointerLeave: stop,
    onPointerCancel: stop,
  };
}

/** Sepete eklendi geri bildirimi: 900ms checkmark */
function useAddFeedback() {
  const [done, setDone] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const fire = useCallback(() => {
    clearTimeout(timer.current);
    setDone(true);
    timer.current = setTimeout(() => setDone(false), 900);
  }, []);
  return [done, fire];
}

/** Zincir rozetleri — kampanya farkli renkte */
export function DiscountChips({ discounts = [], campaign }) {
  if (!discounts.length) return null;
  return (
    <span className={styles.chainRow}>
      {discounts.map((d, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className={styles.chainPlus}>+</span>}
          <span
            className={`${styles.chainChip} ${d.kind === 'general' ? styles.chipCampaign : styles.chipGroup}`}
            title={d.kind === 'general'
              ? 'Kampanyalı'
              : 'İndirimli'}
          >
            %{String(d.rate).replace('.', ',')}
          </span>
        </React.Fragment>
      ))}
    </span>
  );
}

/** Aciklama yanindaki rozetler */
export function ProductBadges({ product }) {
  const zincir = product.discounts || [];
  // Raptiye  : cariye ozel (urun/grup) indirim
  // KAMPANYALI: genel (tum musterilere acik) kampanya
  const cariyeOzel = zincir.some((d) => d.kind === 'customer');
  const genelKampanya = zincir.some((d) => d.kind === 'general');
  return (
    <span className={styles.metaRow}>
      {cariyeOzel && (
        <span className={styles.pinBadge} title="İndirimli">
          <Pin size={10} />
        </span>
      )}
      {genelKampanya && (
        <span className={styles.campBadge} title="Kampanyalı">KAMPANYALI</span>
      )}
    </span>
  );
}

/** Aciklamanin altindaki OEM satiri */
export function OemLine({ product, limit = 3 }) {
  const oems = (product.oem || []).slice(0, limit);
  if (!oems.length) return null;
  const rest = (product.oem || []).length - oems.length;
  return (
    <span className={styles.oemLine}>
      <span className={styles.oemTag}>OEM</span>
      {oems.map((o) => <span key={o} className={styles.oemChip}>{o}</span>)}
      {rest > 0 && <span className={styles.oemChip}>+{rest}</span>}
    </span>
  );
}

/** Tek depo: durum + adet + sepete ekle */
export function WarehouseCell({
  product, wh, onAddToCart, compact,
  quickOnClick = false, onQuickOpen = () => {},
}) {
  const stock = product[wh.key];
  const [raw, setRaw] = useState('1');
  const [added, fireAdded] = useAddFeedback();
  const out = stock <= 0;
  const qty = Math.min(Math.max(parseInt(raw, 10) || 1, 1), Math.max(stock, 1));

  // Musteri miktari degistirince hizli siparis karti / Detayli Siparis ayni depo+miktarla acilsin.
  // Ilk acilistaki varsayilan 1 yazilmaz (yoksa her hucre birbirinin ustune yazar).
  const ilkRef = useRef(true);
  useEffect(() => {
    if (ilkRef.current) { ilkRef.current = false; return; }
    if (!out) taslakYaz(product.productCode || product.code, wh.key, qty);
  }, [qty]); // eslint-disable-line react-hooks/exhaustive-deps

  const step = useCallback((d) => {
    setRaw((cur) => {
      const n = parseInt(cur, 10) || 1;
      return String(Math.min(Math.max(n + d, 1), Math.max(stock, 1)));
    });
  }, [stock]);

  const onChange = (e) => {
    const v = e.target.value;
    if (v === '' || /^[0-9]{0,5}$/.test(v)) setRaw(v);
  };

  const dec = useCallback(() => step(-1), [step]);
  const inc = useCallback(() => step(1), [step]);
  const holdDec = useHoldRepeat(dec);
  const holdInc = useHoldRepeat(inc);

  return (
    <span className={compact ? styles.whCell : styles.whInline}>
      {compact && <span className={styles.mWhName}>{wh.city}</span>}
      <span className={styles.whHead} title={out ? 'Stok yok' : `${formatNumber(stock)} adet stokta`}>
        <span className={out ? styles.dotOut : styles.dotIn} />
        <span className={`${styles.whLabelSm} ${out ? styles.whOut : styles.whIn}`}>
          {out ? 'Stok Yok' : 'Mevcut'}
        </span>
      </span>
      <span className={styles.qty}>
        <button type="button" className={styles.qtyBtn} {...holdDec}
                disabled={qty <= 1 || out} aria-label="Azalt"><Minus size={12} /></button>
        <input
          className={styles.qtyInput} type="text" inputMode="numeric"
          value={out ? '0' : raw} disabled={out}
          onChange={onChange} onBlur={() => setRaw(String(qty))}
          onFocus={(e) => e.target.select()}
          aria-label={`${wh.label} adet`}
        />
        <button type="button" className={styles.qtyBtn} {...holdInc}
                disabled={qty >= stock || out} aria-label="Artır"><Plus size={12} /></button>
      </span>
      <button
        type="button"
        className={`${styles.whAdd} ${added ? styles.btnDone : ''}`}
        disabled={out}
        onClick={(e) => {
          if (quickOnClick) {
            const r = e.currentTarget.getBoundingClientRect();
            onQuickOpen(product, { x: r.right, y: r.top + r.height / 2 });
            return;
          }
          onAddToCart(product, qty, wh);
          fireAdded();
        }}
        title={out
          ? 'Bu depoda stok yok'
          : quickOnClick
            ? `${product.description} — hızlı sipariş`
            : `${wh.label} deposundan sepete ekle`}
        aria-label={quickOnClick
          ? `${product.description} hızlı sipariş panelini aç`
          : `${wh.label} deposundan sepete ekle`}
      >
        {added ? <Check size={13} /> : <ShoppingCart size={13} />}
        {compact && <span className={styles.whAddText}>{added ? 'EKLENDİ' : 'EKLE'}</span>}
      </button>
    </span>
  );
}

/** Sadece mevcutluk gosteren stok hucresi */
export function StockPill({ qty }) {
  const out = qty <= 0;
  return (
    <span className={`${styles.stockPill} ${out ? styles.pillOut : styles.pillIn}`}>
      {out ? <XCircle size={13} /> : <CheckCircle2 size={13} />}
      {out ? 'Stok Yok' : 'Mevcut'}
    </span>
  );
}

function Thumb({ product, onOpenDetail }) {
  const [pos, setPos] = useState(null);

  const show = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const top = Math.min(Math.max(r.top - 60, 12), Math.max(window.innerHeight - 250, 12));
    setPos({ top, left: r.right + 12 });
  };

  return (
    <>
      <button
        type="button" className={styles.thumbBox}
        onClick={() => onOpenDetail(product)}
        onMouseEnter={show} onMouseLeave={() => setPos(null)}
        aria-label={`${product.description} detayı`}
      >
        {product.image ? (
          <img
            className={styles.thumb}
            src={product.image}
            alt={product.description}
            loading="lazy"
            decoding="async"
            width="40"
            height="40"
          />
        ) : (
          <span className={styles.thumbFallback}><Package size={20} /></span>
        )}
      </button>
      {pos && (
        <div className={styles.previewCard} style={{ top: pos.top, left: pos.left }}>
          <div className={styles.previewBox}>
            {product.image ? (
              <img className={styles.previewImg} src={product.image} alt="" decoding="async" />
            ) : (
              <span className={styles.thumbFallback}><Package size={64} strokeWidth={1.1} /></span>
            )}
          </div>
          <div className={styles.previewName}>{product.description}</div>
          <div className={styles.previewCode}>{product.productCode}</div>
        </div>
      )}
    </>
  );
}

function ProductRow({
  product, onAddToCart, onOpenDetail,
  showOem = true, eurRate = 0, hidden = {}, simpleStock = false, showActions = false,
  onQuickEnter = () => {}, onQuickLeave = () => {}, isActive = false,
  quickOnClick = false, onQuickOpen = () => {},
}) {
  const [added, fireAdded] = useAddFeedback();
  const eur = (v) => {
    if (eurRate <= 0) return null;
    // fiyat yoksa (null) euro satirinda da '-' gosterilir
    const bos = v === null || v === undefined || v === '';
    return (
      <span className={styles.eur}>
        {bos ? '-' : `${formatNumber(v / eurRate, 2)} €`}
      </span>
    );
  };

  const firstAvailable = WAREHOUSES.find((w) => product[w.key] > 0);

  return (
    <tr
      className={`${styles.row} ${isActive ? styles.rowActive : ''}`}
      onMouseEnter={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        onQuickEnter(product, { x: r.right, y: r.top + r.height / 2 });
      }}
      onMouseLeave={onQuickLeave}
    >
      <td><Thumb product={product} onOpenDetail={onOpenDetail} /></td>
      <td>
        <button type="button" className={styles.code} onClick={() => onOpenDetail(product)}>
          {product.productCode}
        </button>
      </td>
      <td>
        <span className={styles.descCell}>
          <span className={styles.descLine}>
            <span className={styles.desc} title={product.description}>{product.description}</span>
            <ProductBadges product={product} />
          </span>
          {showOem && <OemLine product={product} />}
        </span>
      </td>
      <td className={styles.center}><span className={styles.group} title={product.groupCode}>{product.groupCode}</span></td>
      {!hidden.chain && (
        <td><DiscountChips discounts={product.discounts} campaign={product.campaign} /></td>
      )}
      <td>
        <span className={styles.priceCell}>
          <span className={styles.gross}>{fiyatTRY(product.grossPrice)}</span>
          {eur(product.grossPrice)}
        </span>
      </td>
      <td>
        <span className={styles.priceCell}>
          <span className={styles.net}>{fiyatTRY(product.netPrice)}</span>
          {eur(product.netPrice)}
        </span>
      </td>
      {!hidden.vat && (
        <td>
          <span className={styles.priceCell}>
            <span className={styles.vat}>{fiyatTRY(product.vatPrice)}</span>
            {eur(product.vatPrice)}
          </span>
        </td>
      )}
      {WAREHOUSES.map((wh) => (
        <td key={wh.key} className={styles.center}>
          {simpleStock
            ? <StockPill qty={product[wh.key]} />
            : <WarehouseCell product={product} wh={wh} onAddToCart={onAddToCart}
                               quickOnClick={quickOnClick} onQuickOpen={onQuickOpen} />}
        </td>
      ))}
      {showActions && (
        <td className={styles.center}>
          <span className={styles.actionCell}>
            <button type="button" className={styles.iconAct}
                    onClick={() => onOpenDetail(product)} title="Ürün detayı" aria-label="Ürün detayı">
              <Eye size={15} />
            </button>
            <button
              type="button"
              className={`${styles.cartAct} ${added ? styles.btnDone : ''}`}
              disabled={!firstAvailable}
              onClick={() => { onAddToCart(product, 1, firstAvailable); fireAdded(); }}
              title={firstAvailable ? `${firstAvailable.label} deposundan sepete ekle` : 'Stok yok'}
              aria-label="Sepete ekle"
            >
              {added ? <Check size={15} /> : <ShoppingCart size={15} />}
            </button>
          </span>
        </td>
      )}
    </tr>
  );
}

/** Mobil kart */
export function ProductCard({ product, onAddToCart, onOpenDetail }) {
  return (
    <div className={styles.mCard}>
      <div className={styles.mTop}>
        <Thumb product={product} onOpenDetail={onOpenDetail} />
        <div className={styles.mBody}>
          <div className={styles.mCode}>{product.productCode}</div>
          <div className={styles.mDesc}>{product.description}</div>
          <ProductBadges product={product} />
          <OemLine product={product} limit={2} />
        </div>
      </div>

      <div className={styles.mPrices}>
        <span className={styles.mPrice}>
          <span className={styles.mPriceLabel}>Brüt</span>
          <span className={styles.gross}>{fiyatTRY(product.grossPrice)}</span>
        </span>
        <span className={styles.mPrice}>
          <span className={styles.mPriceLabel}>Net</span>
          <span className={styles.net}>{fiyatTRY(product.netPrice)}</span>
        </span>
        <span className={styles.mPrice}>
          <span className={styles.mPriceLabel}>KDV'li</span>
          <span className={styles.vat}>{fiyatTRY(product.vatPrice)}</span>
        </span>
        <span className={styles.mPrice}>
          <span className={styles.mPriceLabel}>İndirim</span>
          <DiscountChips discounts={product.discounts} campaign={product.campaign} />
        </span>
      </div>

      <div className={styles.mWh}>
        {WAREHOUSES.map((wh) => (
          <div key={wh.key} className={styles.mWhBox}>
            <WarehouseCell product={product} wh={wh} onAddToCart={onAddToCart} compact />
          </div>
        ))}
      </div>
    </div>
  );
}

export default React.memo(ProductRow);
